// =============================================================================
// Żywość agenta Receptora z okresem pulsu (plan 59, K7)
//
// Agent 0.8.0 pulsuje co 5 s i zgłasza okres (`puls_okres_s`, migracja 840),
// a receptor-core zapisuje `last_seen_at` przy zmianie treści albo co ≤ 30 s
// (D3), nie przy każdym pulsie. Flota jest mieszana — agenty 0.7.x pulsują co
// 60 s i okresu nie podają — więc próg „offline” liczy się z okresu KAŻDEGO
// agenta: `max(90 s, 3 × okres)`. 90 s = zapis żywotności co ≤ 30 s + zegar
// listy (`ZEGAR_MS`, 30 s) + 30 s zapasu; NULL = 60 s, czyli dzisiejsze 3 min.
//
// Agentów druku (`printing/status.ts`, `stanAgenta`) to nie dotyczy — pulsują
// co 60 s i zostają przy stałym progu 3 min.
// =============================================================================

import {
  PULS_OKRES_MAX_S,
  PULS_OKRES_MIN_S,
  PULS_OKRES_NIEZNANY_S,
} from '@insolitum/types/receptor';

import type { StanAgenta } from '../printing/status';

/** Podłoga progu „offline” — zapis żywotności co ≤ 30 s + zegar listy 30 s + zapas. */
export const PROG_OFFLINE_PODLOGA_MS = 90_000;

/** Okres pulsu do progu — spoza 5–300 s przycięty tak jak w usłudze (P12). */
export function okresPulsuS(puls_okres_s: number | null | undefined): number {
  if (typeof puls_okres_s !== 'number' || !Number.isFinite(puls_okres_s)) {
    return PULS_OKRES_NIEZNANY_S;
  }
  return Math.min(PULS_OKRES_MAX_S, Math.max(PULS_OKRES_MIN_S, Math.round(puls_okres_s)));
}

/** `max(90 s, 3 × okres)` — sufit 3 × 300 s = 15 min, czyli zepsuty agent nie zamaskuje ciszy. */
export function progOfflineAgenta(puls_okres_s: number | null | undefined): number {
  return Math.max(PROG_OFFLINE_PODLOGA_MS, 3 * okresPulsuS(puls_okres_s) * 1000);
}

export interface AgentDoStanu {
  status: string;
  last_seen_at: string | null;
  puls_okres_s?: number | null;
}

/**
 * Stan agenta Receptora: cykl życia z `status`, żywość z `last_seen_at`
 * względem progu z JEGO okresu pulsu.
 */
export function stanAgentaReceptora(agent: AgentDoStanu, teraz: number = Date.now()): StanAgenta {
  if (agent.status === 'revoked') return 'revoked';
  if (agent.status === 'pending') return 'pending';
  if (!agent.last_seen_at) return 'offline';
  const widziany = new Date(agent.last_seen_at).getTime();
  if (!Number.isFinite(widziany)) return 'offline';
  return teraz - widziany <= progOfflineAgenta(agent.puls_okres_s) ? 'online' : 'offline';
}

export const KOMUNIKAT_AGENT_OFFLINE = 'Agent Receptora jest offline — PIN czeka, aż wróci.';
export const KOMUNIKAT_AGENT_OFFLINE_OSOBY =
  'Agent Receptora jest offline — lista osób przyjdzie, gdy wróci.';
export const KOMUNIKAT_CZEKANIA_SZYBKO = 'Czekam na agenta Receptora (zwykle kilka sekund)…';
export const KOMUNIKAT_CZEKANIA_WOLNO = 'Czekam na agenta Receptora (do ~1 min)…';

/** Okres, przy którym wolno obiecać „kilka sekund”. */
export const OKRES_SZYBKI_MAKS_S = 10;

/**
 * Zdanie przy czekaniu na zadanie agenta (P33). Agent nieznany → zdanie
 * ostrożne („do ~1 min”); offline → wprost, że czeka na powrót; okres ≤ 10 s →
 * „zwykle kilka sekund” — „zwykle”, bo pas pilny obejmuje wyłącznie wynik kroku,
 * a inne zadania czekają też na koniec porcji.
 */
export function komunikatCzekania(
  agent: AgentDoStanu | null | undefined,
  teraz: number = Date.now(),
  /** Na co czekamy — zdanie „offline” mówi, co przyjdzie po powrocie agenta. */
  czego: 'pin' | 'osoby' = 'pin'
): string {
  if (!agent) return KOMUNIKAT_CZEKANIA_WOLNO;
  if (stanAgentaReceptora(agent, teraz) === 'offline') {
    return czego === 'osoby' ? KOMUNIKAT_AGENT_OFFLINE_OSOBY : KOMUNIKAT_AGENT_OFFLINE;
  }
  return okresPulsuS(agent.puls_okres_s) <= OKRES_SZYBKI_MAKS_S
    ? KOMUNIKAT_CZEKANIA_SZYBKO
    : KOMUNIKAT_CZEKANIA_WOLNO;
}

/**
 * Zdanie pod pustą listą osób, zanim cokolwiek zlecono — o tym samym rytmie co
 * {@link komunikatCzekania}, tylko w trybie oznajmującym.
 */
export function zdanieORytmieAgenta(
  agent: AgentDoStanu | null | undefined,
  teraz: number = Date.now()
): string {
  if (!agent) return 'Agent odpowiada w ciągu około minuty.';
  if (stanAgentaReceptora(agent, teraz) === 'offline') {
    return 'Agent Receptora jest offline — odpowie, gdy wróci.';
  }
  return okresPulsuS(agent.puls_okres_s) <= OKRES_SZYBKI_MAKS_S
    ? 'Agent odpowiada zwykle w kilka sekund.'
    : 'Agent odpowiada w ciągu około minuty.';
}
